/**
  * @class meleButton
  * @description
  *
  * # Purpose
  * Implements `<mele-button>` as a clickable button element with text and/or icon.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML = '<mele-button label="My Button"></mele-button>>';
  * ```
  * Create element in JavaScript:
  * ```
  * const el = elParent.appendChildren({
  *   tag: 'mele-button',
  *   label: 'country.SK',
  *   iconleft: 'plain_clear',
  * });
  * ```
  * Programmatically invoke click:
  * ```
  * el.click();
  * ```
  *
  * # Implementation notes
  * When clicked and not disabled, sends mmButtonClicked to Mosy module where the tag belongs
  */

import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  type: { description: 'type of button', type: 'string', enum: ['action', 'actionbar', 'toolbar', 'icon', 'back'] },
  color: { description: 'color of the button', type: 'string' },
  prefix: { type: 'string', description: 'Sets the prefix for automatic filling of attributes' },
  label: { description: 'main button label', type: 'string' },
  label2: { description: 'secondary button label', type: 'string' },
  tooltip: { description: 'tooltip for button', type: 'string' },
  iconleft: { description: 'name of icon on the left', type: 'string' },
  iconright: { description: 'name of icon on the right', type: 'string' },
  selected: { description: 'indicates the button is selected', type: 'boolean' },
  interclick: { description: 'time limit for multiclick', type: 'integer', default: 0, noattribute: true, readonly: true },
  interlong: { description: 'time limit for long press', type: 'integer', default: 0, noattribute: true, readonly: true },
  action: { type: 'string', description: 'action when button is clicked and eventButtonClicked is not defined', enum: ['new', 'clear', 'undo', 'submit'] },
  script: { type: 'string', description: 'workflow script to be executed on button click' },
  eventButtonClicked: { type: 'function', description: 'Called when button is clicked; if not set, will publish mmButtonClicked message to parent module' },
  eventButtonDown: { type: 'function', description: 'Called when mouse is down on button; if not set, will publish mmButtonDown message to parent module' },
}, meleElement._properties)

let mele_label1_template
let mele_label2_template
let mele_labels1_template
let mele_labels2_template

// Module-level guard that swallows the trailing native click the browser still fires after
// a long-press resolved early, or after a press that turned into a scroll. Without this,
// long-press would resolve twice (once as long-press, once as click). Armed only when needed
// — a normal tap leaves the guard inert so clicks on other elements (inputs, modals, etc.)
// are never affected.
const _ghostGuard = { until: 0, suppress: false }
let _ghostGuardInstalled = false

function installGhostClickGuard() {
  if (_ghostGuardInstalled) return
  _ghostGuardInstalled = true
  document.addEventListener('click', (e) => {
    if (!_ghostGuard.suppress) return
    if (Date.now() > _ghostGuard.until) return
    _ghostGuard.suppress = false
    _ghostGuard.until = 0
    e.stopImmediatePropagation()
    e.preventDefault()
  }, true)
}

export default class meleButton extends meleElement {
  init() {
    if(!mele_label1_template) {
      mele_label1_template = this.createElementFast('mele-label')
      mele_label2_template = this.createElementFast('mele-label2')
      mele_labels1_template = this.createElementFast('mele-labels')
      mele_labels1_template.innerHTML = '<mele-label></mele-label>'
      mele_labels2_template = this.createElementFast('mele-labels')
      mele_labels2_template.innerHTML = '<mele-label2></mele-label2>'
    }

    this.initClassScope()

    super.init()
    this.resetInteractionState()

    // Pointer events track the press (long-press timer, scroll-to-cancel, eventButtonDown)
    this.addEventListener('pointerdown', this.handlePointerDown.bind(this))
    this.addEventListener('pointermove', this.handlePointerMove.bind(this))
    this.addEventListener('pointerup', this.handlePointerUp.bind(this))
    this.addEventListener('pointercancel', this.handlePointerCancel.bind(this))
    // Native click drives the actual click behavior — handles mouse, touch tap (browser-
    // synthesized), keyboard activation, and programmatic .click() uniformly. Subclasses
    // (meleItem, meleModal, melePopup, …) hook in via onclick() which meleElement registers
    // as a click listener automatically.
    this.addEventListener('click', this.handleClickEvent.bind(this))
    this.addEventListener('contextmenu', (e) => {
      if (this._interactionState.isLongPress) {
        e.preventDefault()
      }
    })
    installGhostClickGuard()
  }

  initClassScope() {
    this.classList.add(this.classScope('wrapper'))
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "prefix"; same effect as changed language
  */
  prefixChanged() {
    this.invokeDependentChanges('prefix')
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "key"; same effect as changed language; will refresh list of items
  */
  keyChanged() {
    this.invokeDependentChanges('key')
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "selected"
  * we do refresh of all language-dependent attributes as any may contain ${selected} variable inside
  */
  selectedChanged() {
    this.invokeDependentChanges('selected')
  } // selectedChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label"
  */
  labelChanged(value) {
    const html = this.translateAndSetDependencies(value, false, 'label')
    if(html==='') {
      if(!this._labels) return
      if(this._label) {
        this._label.deleteElement()
        delete this._label
      }
      if(this._labels && !this._label2) {
        // delete holder for labels if neither label nor label2 exist
        this._labels.deleteElement()
        delete this._labels
      }
    }else{
      this._createLabel(html)
    }
  } // labelChanged

  _createLabel(html) {
    html = String(html??'').replace(/\\n/g, '<br>')
    if(this._labels) {
      if(this._label) {
        this._label.innerHTML = html
      }else{
        this._label = mele_label1_template.cloneNode()
        this._label.classList.add(this.classScope('label'))
        this._label.innerHTML = html
        this._labels.appendChild(this._label)
      }
    }else{
      this._labels = mele_labels1_template.cloneNode(true)
      this._labels.classList.add(this.classScope('labels'))
      this._label = this._labels.children[0]
      this._label.classList.add(this.classScope('label'))
      this._label.innerHTML = html
      this.appendChild(this._labels)
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "label2"
  */
  label2Changed(value) {
    const html = this.translateAndSetDependencies(value, false, 'label2')
    if(html==='') {
      if(!this._labels) return
      if(this._label2) {
        this._label2.deleteElement()
        delete this._label2
      }
      if(this._labels && !this._label) {
        // delete holder for labels if neither label nor label2 exist
        this._labels.deleteElement()
        delete this._labels
      }
    }else{
      this._createLabel2(html)
    }
  } // label2Changed

  _createLabel2(html) {
    html = String(html??'').replace(/\n|\\n/g, '<br>')
    if(this._labels) {
      if(this._label2) {
        this._label2.innerHTML = html
      }else{
        this._label2 = mele_label2_template.cloneNode()
        this._label2.classList.add(this.classScope('secondary-label'))
        this._label2.innerHTML = html
        this._labels.appendChild(this._label2)
      }
    }else{
      this._labels = mele_labels2_template.cloneNode(true)
      this._labels.classList.add(this.classScope('labels'))
      this._label2 = this._labels.children[0]
      this._label2.classList.add(this.classScope('secondary-label'))
      this._label2.innerHTML = html
      this.appendChild(this._labels)
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "tooltip"
  */
  tooltipChanged(value) {
    const html = this.translateAndSetDependencies(value, false, 'tooltip')
    if(html==='') {
      this.removeAttr('title')
    }else {
      this.title = html
    }
  } // tooltipChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "iconleft"
  */
  iconleftChanged(value) {
    let elIcon = ''
    if(/base64/.test(value)) {
      elIcon = new Image()
      elIcon.src = /^data:/.test(value) ? value: 'data:'+value
    }else {
      elIcon = this.getImageAndSetDependencies(value, 'iconleft')
    }
    if(!elIcon) {
      if(!this._iconleft) return
      this._iconleft.deleteElement()
      delete this._iconleft
    }else{
      if(!this._iconleft) {
        this._iconleft = this.createElementFast('mele-icon')
        this._iconleft.classList.add('left')
      }else{
        this._iconleft.innerHTML = ''
      }
      this._iconleft.appendChild(elIcon)
      this._iconleft.classList.add(this.classScope('icon'))
      this.appendChild(this._iconleft)
    }
  } // iconleftChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "iconright"
  */
  iconrightChanged(value) {
    let elIcon = ''
    if(/base64/.test(value)) {
      elIcon = new Image()
      elIcon.src = /^data:/.test(value) ? value: 'data:'+value
    }else {
      elIcon = this.getImageAndSetDependencies(value, 'iconright')
    }
    if(!elIcon) {
      if(!this._iconright) return
      this._iconright.deleteElement()
      delete this._iconright
    }else{
      if(!this._iconright) {
        this._iconright = this.createElementFast('mele-icon')
        this._iconright.classList.add('right')
      }else{
        this._iconright.innerHTML = ''
      }
      this._iconright.appendChild(elIcon)
      this._iconright.classList.add(this.classScope('icon'))
      this.appendChild(this._iconright)
    }
  } // iconrightChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Handle change of attribute "color"
  */
  colorChanged(value) {
    let className = this.className.replace(/\bcolor-\S+/, '')
    const color = this.translateAndSetDependencies(value, false, 'color')
    if(color!=='') className += ' color-'+color
    this.className = className
      .replace(/\s+/g, ' ')
      .replace(/^\s+/, '')
      .replace(/\s+$/, '')
  } // colorChanged

  classChanged() {
    // Ensure button-wrapper class is always present (it may be lost when class attribute is set)
    const wrapperClass = this.classScope('wrapper')
    if (!this.classList.contains(wrapperClass)) {
      this.classList.add(wrapperClass)
    }
    this.colorChanged(this.color)
  }

  resetInteractionState() {
    this._interactionState = {
      isPressed: false,
      startX: 0,
      startY: 0,
      isScrolling: false,
      isLongPress: false,
      pressTimer: null,
      pointerType: null,
      pointerId: null,
    }

    // Keep separate multiclick state that persists across individual presses
    if (!this._multiclickState) {
      this._multiclickState = {
        clickCount: 0,
        lastClickTime: 0,
        clickTimer: null,
      }
    }
  }

  clearTimers() {
    if (this._interactionState.pressTimer) {
      clearTimeout(this._interactionState.pressTimer)
      this._interactionState.pressTimer = null
    }
  }

  clearClickTimer() {
    if (this._multiclickState && this._multiclickState.clickTimer) {
      clearTimeout(this._multiclickState.clickTimer)
      this._multiclickState.clickTimer = null
    }
  }

  handlePointerDown(event) {
    if (this.disabled || this.hidden) return
    if (!event.isPrimary) return

    // Suppress duplicate mouse-typed pointerdown that some platforms synthesize after touch
    if (event.pointerType === 'mouse' && this._lastPointerWasTouch) return
    if (event.pointerType === 'touch') {
      this._lastPointerWasTouch = true
      clearTimeout(this._touchGuardTimer)
      this._touchGuardTimer = setTimeout(() => { this._lastPointerWasTouch = false }, 600)
    }

    // Only preventDefault for actual <mele-button> targets — NOT for subclasses like
    // <mele-input> / <mele-item> where the browser's default action (focus, text caret
    // placement) is needed. meleInput in particular extends meleItem → meleButton, so it
    // inherits these pointer handlers but must not have its native focus blocked.
    const target = event.target
    const isButton = target.tagName === 'MELE-BUTTON' || target.closest('mele-button')
    if (isButton) {
      event.preventDefault()
    }

    this.startPress(event.clientX, event.clientY, event.pointerId)
  }

  handlePointerMove(event) {
    if (!this._interactionState.isPressed || event.pointerId !== this._interactionState.pointerId) return

    const deltaX = Math.abs(event.clientX - this._interactionState.startX)
    const deltaY = Math.abs(event.clientY - this._interactionState.startY)

    // If moved too far, consider it scrolling/dragging
    if (deltaX > 10 || deltaY > 10) {
      this._interactionState.isScrolling = true
      this.clearTimers()
    }
  }

  handlePointerUp(event) {
    if (!this._interactionState.isPressed || event.pointerId !== this._interactionState.pointerId) return

    const wasScrolling = this._interactionState.isScrolling
    const wasLongPress = this._interactionState.isLongPress

    this._interactionState.isPressed = false
    this.clearTimers()

    if (wasScrolling || wasLongPress) {
      // Browser will still fire a trailing click; suppress it so long-press doesn't also
      // register as a regular click, and a touch-cancelled-by-scroll doesn't fire either.
      _ghostGuard.suppress = true
      _ghostGuard.until = Date.now() + 600
    }

    // Don't trigger the click here — the native click event arrives next and drives
    // handleClickEvent → handleClick, keeping subclass onclick() and our click logic
    // bound to a single authoritative trigger.
    this.resetInteractionState()
  }

  handleClickEvent() {
    if (this.disabled || this.hidden) return
    // The capture-phase guard at document level has already filtered ghost clicks and the
    // trailing clicks after long-press/scroll. Anything reaching here is a real click.
    this.handleClick()
  }

  handlePointerCancel(event) {
    if (event.pointerId !== this._interactionState.pointerId) return
    this.cancelPress()
  }

  startPress(clientX, clientY, pointerId) {
    this.clearTimers()

    this._interactionState.isPressed = true
    this._interactionState.startX = clientX
    this._interactionState.startY = clientY
    this._interactionState.isScrolling = false
    this._interactionState.isLongPress = false
    this._interactionState.pointerId = pointerId

    // Start long press timer
    if (this.interlong > 0) {
      this._interactionState.pressTimer = setTimeout(() => {
        this.debug('Long press timer fired')
        if (this._interactionState.isPressed && !this._interactionState.isScrolling) {
          this._interactionState.isLongPress = true
          this.handleLongPress()
        }
      }, this.interlong)
    }

    // Call button down event with NEXT count (not current)
    // This is what the count will be when the press completes
    this.eventButtonDown(this, this._multiclickState.clickCount + 1)
  }

  cancelPress() {
    this.clearTimers()
    this.resetInteractionState()
  }

  handleClick() {
    const now = Date.now()
    const timeSinceLastClick = now - this._multiclickState.lastClickTime
    // If too much time passed, start fresh sequence
    if (timeSinceLastClick > this.interclick) {
      this._multiclickState.clickCount = 0
    }

    // Increment click count
    this._multiclickState.clickCount++
    this._multiclickState.lastClickTime = now

    // Clear any existing click timer
    this.clearClickTimer()

    // Capture the current count for the timer callback
    const currentCount = this._multiclickState.clickCount

    if (this.interclick === 0) {
      // Fire synchronously
      this._multiclickState.clickCount = 0
      this._multiclickState.lastClickTime = 0
      this.resolveClick(currentCount, false)
    } else {
      this._multiclickState.clickTimer = setTimeout(() => {
        this.resolveClick(currentCount, false)
        this._multiclickState.clickCount = 0
        this._multiclickState.lastClickTime = 0
        this._multiclickState.clickTimer = null
      }, this.interclick)
    }
  }

  handleLongPress() {
  // For long press, use current multiclick count + 1
    const count = this._multiclickState.clickCount + 1

    this.clearTimers()
    this.clearClickTimer()

    this.resolveClick(count, true)

    // Reset both states after long press
    this._multiclickState.clickCount = 0
    this._multiclickState.lastClickTime = 0
    this.resetInteractionState()
  }

  resolveClick(count, isLong) {
    if (this.script) {
      try {
        const scripts = _.get(this.closest('.mele-window,.mele-modal'), 'module.workflow.parsed_scripts', {})
        const fcn = scripts[this.script]
        if (typeof fcn === 'function') {
          const holder = this.closest('.mele-container:not([propagate])')
          fcn.call(this, holder)
        }
      } catch (e) {
        console.error('invalid button script: ' + e.message)
      }
    } else if (this.action) {
      const holder = this.closest('.mele-container:not([propagate])')
      const action = 'form' + _.capitalize(this.action.replace(/\W/g, ''))
      if (typeof holder[action] === 'function') {
        return holder[action](this)
      }
    } else {
      if (typeof this.eventButtonClicked === 'function') {
        this.eventButtonClicked(this, count, isLong)
      }
    }
  }

  eventButtonClicked(self, count, isLong) {
    let holder = this.closest('.mele-container:not([propagate])')
    holder = holder ? holder.key || '' : ''
    this.publish('mmButtonClicked', { holder, button: this.key, count, long: isLong })
  }

  eventButtonDown(self, count) {
    let holder = this.closest('.mele-container:not([propagate])')
    holder = holder ? holder.key || '' : ''
    this.publish('mmButtonDown', { holder, button: this.key, count })
  }

  disconnectedCallback() {
    if(this.isParkedDisconnect && this.isParkedDisconnect()) return
    this.clearTimers()
    this.clearClickTimer()
    // Clean up pointer event listeners
    this.removeEventListener('pointerdown', this.handlePointerDown)
    this.removeEventListener('pointermove', this.handlePointerMove)
    this.removeEventListener('pointerup', this.handlePointerUp)
    this.removeEventListener('contextmenu', this.handleContextMenu)
  }

  click() {
    if (this.disabled || this.hidden) return
    this.resolveClick(1, false)
  } // click
} // class meleButton

meleButton._properties = PROPERTIES
meleButton.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-button', meleButton)
